import moment from "moment";
import {
  ExternalProduct,
  ExternalProductFieldDefinition,
} from "../../api/services/ProductsService";
import { ExternalBondRecordRequest } from "../../api/services/ExternalBondRecordsService";
import {
  getUiSchemaBondOptions,
  getUiSchemaPrincipal,
  mappingWarnings,
  Step,
  termsAndConditionsProperties,
} from "./FormBondApp.constants";
import { BondInfo, FormData, StepHeader, StepItem } from "./FormBondApp.types";

const DATE_FORMAT = "YYYY-MM-DD";

export const formatDollars = (amount: number) =>
  `$${amount.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

export const formatCents = (cents: number) => formatDollars(cents / 100);

const formatTerm = (years: number) => `${years} ${years === 1 ? "year" : "years"}`;

/**
 * An external product quotes terms as prose ("1 year", "2 years") but orders
 * against dates, so the leading count is what we work with.
 */
export const parseTermInYears = (term: string): number | null => {
  const years = parseInt(term, 10);

  return Number.isNaN(years) ? null : years;
};

export const getExternalTermOptions = (externalProduct: ExternalProduct) => {
  const increments = externalProduct.year_increments?.length
    ? externalProduct.year_increments
    : [externalProduct.term_length];

  return increments.filter(years => years > 0);
};

export const getExternalPremiumInCents = (
  externalProduct: ExternalProduct,
  bondAmount?: number,
  termInYears?: number
): number | null => {
  if (!bondAmount || !termInYears) {
    return null;
  }

  const quote = externalProduct.price_quotes?.find(
    ({ bond_amount, term }) =>
      bond_amount === bondAmount && parseTermInYears(term) === termInYears
  );

  return quote ? quote.premium_in_cents : null;
};

/**
 * The integration prices per term rather than per end date, so the term the
 * applicant picked is what sets the expiration.
 *
 * The obligation's `expiration_directive` / `expiration_day_of_year` are passed
 * through untouched for now — we have no documented vocabulary for them beyond
 * "none".
 */
export const getExternalExpirationDate = (
  effectiveDate: string,
  termInYears: number
) => moment(effectiveDate).add(termInYears, "years").format(DATE_FORMAT);

const externalFieldSchema = ({
  field_type,
  label,
  name,
  tooltip,
}: ExternalProductFieldDefinition) => {
  const base = {
    title: label || name,
    readOnly: false,
    ...(tooltip ? { description: tooltip } : {}),
  };

  if (field_type === "date" || field_type === "ISO8601") {
    return { ...base, type: "string", format: "date" };
  }

  if (field_type === "numeric") {
    return { ...base, type: "number" };
  }

  return { ...base, type: "string" };
};

/**
 * The obligation's own form fields, all of which the applicant must answer.
 */
export const buildExternalFormSpecificSchema = (
  externalProduct: ExternalProduct
) => {
  const fields = externalProduct.form_specific_fields;

  if (!fields?.length) {
    return undefined;
  }

  return {
    type: "object",
    required: fields.map(({ name }) => name),
    properties: fields.reduce(
      (properties: { [key: string]: any }, field) => ({
        ...properties,
        [field.name]: externalFieldSchema(field),
      }),
      {}
    ),
  };
};

const hasBondAmountOptions = (externalProduct?: ExternalProduct | null) =>
  Boolean(externalProduct?.bond_amounts?.length);

export const buildExternalBondOptionsSchema = (
  externalProduct: ExternalProduct
) => {
  const bondAmounts = externalProduct.bond_amounts ?? [];
  const terms = getExternalTermOptions(externalProduct);

  return {
    type: "object",
    required: [
      "bond_amount",
      "term_in_years",
      "effective_date",
      "agreed_to_terms_and_conditions",
    ],
    properties: {
      bond_amount: {
        type: "number",
        title: "Bond Amount",
        ...(hasBondAmountOptions(externalProduct)
          ? {
              enum: bondAmounts,
              enumNames: bondAmounts.map(formatDollars),
            }
          : { minimum: 1 }),
        ...(bondAmounts.length === 1 ? { default: bondAmounts[0] } : {}),
        readOnly: false,
      },
      term_in_years: {
        type: "string",
        title: "Term",
        enum: terms.map(String),
        enumNames: terms.map(formatTerm),
        ...(terms.length === 1 ? { default: String(terms[0]) } : {}),
        readOnly: false,
      },
      effective_date: {
        type: "string",
        title: "Effective Date",
        format: "date",
        default: moment().format(DATE_FORMAT),
        readOnly: false,
      },
      ...termsAndConditionsProperties(),
    },
  };
};

export const getExternalSchemaForStep = (
  step: Step,
  externalProduct: ExternalProduct
) => {
  if (step === Step.EXTERNAL_BOND_SPECIFIC) {
    return buildExternalFormSpecificSchema(externalProduct);
  }

  if (step === Step.EXTERNAL_BOND_OPTIONS) {
    return buildExternalBondOptionsSchema(externalProduct);
  }

  return undefined;
};

export const getExternalUiSchemaForStep = (
  step: Step,
  externalProduct?: ExternalProduct | null
) => {
  if (step === Step.BOND_APPLICANT || step === Step.BOND_BUSINESS) {
    return getUiSchemaPrincipal();
  }

  if (step === Step.EXTERNAL_BOND_OPTIONS) {
    return {
      ...getUiSchemaBondOptions(),
      // a listed amount is picked from a select, which RJSF renders for an enum
      ...(hasBondAmountOptions(externalProduct)
        ? {}
        : { bond_amount: { "ui:widget": "CurrencyWidget" } }),
      "ui:submitButtonOptions": {
        props: {
          className: "submit_button",
        },
        norender: false,
        submitText: "Submit",
      },
    };
  }

  return undefined;
};

type ExternalStepGroup = {
  label: string;
  steps: Step[];
};

const getExternalStepGroups = (
  hasFormSpecificFields: boolean
): ExternalStepGroup[] => [
  {
    label: "Principal Information",
    steps: [
      Step.INSTRUCTIONS,
      Step.START,
      Step.BOND_APPLICANT,
      Step.BOND_BUSINESS,
    ],
  },
  ...(hasFormSpecificFields
    ? [
        {
          label: "Bond specific questions",
          steps: [Step.EXTERNAL_BOND_SPECIFIC],
        },
      ]
    : []),
  {
    label: "Bond Options",
    steps: [Step.EXTERNAL_BOND_OPTIONS],
  },
  {
    // submitting only quotes the bond, so the record it creates goes through
    // review, payment and delivery like any other application
    label: "Bond Purchase",
    steps: [Step.PROCESSING, Step.BOND_PREVIEW, Step.BOND_DELIVERY, Step.COMPLETE],
  },
];

const isPurchaseGroup = (group: ExternalStepGroup) =>
  group.steps.includes(Step.PROCESSING);

const getPrimaryStep = (group: ExternalStepGroup, isBusiness?: boolean) =>
  group.steps.includes(Step.BOND_APPLICANT)
    ? isBusiness
      ? Step.BOND_BUSINESS
      : Step.BOND_APPLICANT
    : group.steps[0];

export const getExternalStepHeader = (
  step: Step,
  hasFormSpecificFields: boolean
): StepHeader => {
  const groups = getExternalStepGroups(hasFormSpecificFields);
  const index = Math.max(
    groups.findIndex(group => group.steps.includes(step)),
    0
  );
  const id = index + 1;
  // a submitted application can't be stepped back into, as in the standard flow
  const previous = isPurchaseGroup(groups[index])
    ? undefined
    : groups[index - 1];

  return {
    id,
    // the first step reads as barely started, the way the standard flow's does
    width: id === 1 ? "10%" : `${Math.round((id / groups.length) * 100)}%`,
    ...(previous
      ? { back: (isBusiness?: boolean) => getPrimaryStep(previous, isBusiness) }
      : {}),
  };
};

export const getExternalSteps = ({
  hasFormSpecificFields,
  step,
  setStep,
  isBusiness,
  formDataBondApplicant,
  formDataBondBusiness,
}: {
  hasFormSpecificFields: boolean;
  step: Step;
  setStep: (step: Step) => void;
  isBusiness?: boolean;
  formDataBondApplicant: FormData;
  formDataBondBusiness: FormData;
}): StepItem[] => {
  const groups = getExternalStepGroups(hasFormSpecificFields);
  const currentIndex = groups.findIndex(group => group.steps.includes(step));
  const hasPrincipal = Boolean(formDataBondApplicant || formDataBondBusiness);

  return groups.map((group, index) => {
    const isPurchase = isPurchaseGroup(group);

    return {
      label: group.label,
      isActive: index <= currentIndex,
      onClick: () => {
        if (isPurchase) {
          return;
        }
        setStep(getPrimaryStep(group, isBusiness));
      },
      // the purchase is only reachable by submitting, and nothing after the
      // principal is answerable until there is a principal
      disabled: isPurchase || (index > 0 && !hasPrincipal),
    };
  });
};

/**
 * Copy for the external-only steps. Every other step, including everything
 * after submission, reads the same as the standard flow.
 */
export const getExternalBondInfo = ({
  step,
  premiumInCents,
  mailingState,
}: {
  step: Step;
  premiumInCents: number | null;
  mailingState: keyof typeof mappingWarnings;
}): BondInfo | undefined => {
  if (step === Step.EXTERNAL_BOND_SPECIFIC) {
    return {
      title: "Bond specific questions",
      subTitle: "Answer bond-specific questions",
      extraInfo: [
        "These questions come from the bond form itself and appear on your issued bond.",
      ],
    };
  }

  if (step === Step.EXTERNAL_BOND_OPTIONS) {
    return {
      title: "Bond Options",
      subTitle: "Almost there!",
      extraInfo: [
        "Enter the bond amount and term required by the obligee, and the date you need the bond to take effect.",
        premiumInCents === null
          ? "Your premium is confirmed when the application is submitted."
          : `Premium for the selected bond amount and term: ${formatCents(
              premiumInCents
            )}.`,
      ],
      stateDisclaimer: mappingWarnings[mailingState] || mappingWarnings.default,
    };
  }

  return undefined;
};

/**
 * Only values the applicant actually answered are sent; the API takes the rest
 * as absent rather than blank, and would reject a blank email outright.
 */
const answered = (value?: string | null) => value?.trim() || undefined;

const serializeFormSpecificFields = (formData: FormData) =>
  Object.entries(formData ?? {}).reduce(
    (fields: { [key: string]: string }, [name, value]) => {
      if (value === null || value === undefined || value === "") {
        return fields;
      }

      return { ...fields, [name]: String(value) };
    },
    {}
  );

export const getExternalBondRecordPayload = ({
  externalProduct,
  isBusiness,
  formDataBondApplicant,
  formDataBondBusiness,
  formDataExternalFormSpecific,
  formDataExternalBondOptions,
}: {
  externalProduct: ExternalProduct;
  isBusiness?: boolean;
  formDataBondApplicant: FormData;
  formDataBondBusiness: FormData;
  formDataExternalFormSpecific: FormData;
  formDataExternalBondOptions: FormData;
}): ExternalBondRecordRequest => {
  const principal = (isBusiness ? formDataBondBusiness : formDataBondApplicant) ?? {};
  const options = formDataExternalBondOptions ?? {};

  const effectiveDate = options.effective_date;
  const termInYears =
    Number(options.term_in_years) || externalProduct.term_length;

  return {
    obligation_id: externalProduct.obligation_id,
    bond_amount: Number(options.bond_amount),

    is_business: Boolean(isBusiness),

    ...(isBusiness
      ? {
          principal_name: principal.business_name,
          principal_dba: principal.business_dba,
        }
      : {
          principal_first_name: principal.first_name,
          principal_middle_name: principal.middle_name,
          principal_last_name: principal.last_name,
          principal_suffix: principal.suffix,
        }),

    principal_address1: principal.mailing_street,
    principal_city: principal.mailing_city,
    principal_state: principal.mailing_state,
    principal_zip: principal.mailing_zip,

    // the integration has nowhere to put these, but the record they create does
    principal_email: answered(principal.email),
    principal_phone: answered(principal.phone),

    effective_date: effectiveDate,
    expiration_date: getExternalExpirationDate(effectiveDate, termInYears),

    state: externalProduct.state,

    agreed_to_terms_and_conditions: Boolean(
      options.agreed_to_terms_and_conditions
    ),
    tc_version: answered(options.tc_version),

    form_specific_fields: serializeFormSpecificFields(
      formDataExternalFormSpecific
    ),
  };
};
